import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Shield, Key, Smartphone, AlertCircle } from 'lucide-react';

export default function SecuritySettings() {
  const { theme } = useTheme();
  const [settings, setSettings] = useState({
    twoFactor: false,
    loginAlerts: true,
    trustedDevices: [
      {
        id: '1',
        name: 'MacBook Pro',
        lastUsed: new Date('2024-03-15T10:30:00'),
        location: 'London, UK'
      },
      {
        id: '2',
        name: 'iPhone 14',
        lastUsed: new Date('2024-03-16T15:45:00'),
        location: 'London, UK'
      }
    ]
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle password change
    console.log('Password changed');
    setShowPasswordModal(false);
  };

  const removeTrustedDevice = (deviceId: string) => {
    setSettings(prev => ({
      ...prev,
      trustedDevices: prev.trustedDevices.filter(device => device.id !== deviceId)
    }));
  };

  return (
    <div className="space-y-8">
      {/* Two-Factor Authentication */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-blue-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Two-Factor Authentication
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className={`font-medium ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Enable 2FA
            </p>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Add an extra layer of security to your account
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, twoFactor: !prev.twoFactor }))}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full
              transition-colors duration-200 ease-in-out focus:outline-none
              ${settings.twoFactor
                ? 'bg-blue-600'
                : theme === 'dark'
                  ? 'bg-gray-700'
                  : 'bg-gray-200'
              }
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition-transform duration-200 ease-in-out
                ${settings.twoFactor ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {settings.twoFactor && (
          <div className={`
            p-4 rounded-lg
            ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}
          `}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Two-factor authentication is enabled. Use an authenticator app to generate codes.
            </p>
            <button
              className={`
                mt-2 px-4 py-2 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'bg-gray-600 text-gray-100 hover:bg-gray-500'
                  : 'bg-white text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              View Recovery Codes
            </button>
          </div>
        )}
      </div>

      {/* Password */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Key className="h-5 w-5 text-green-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Password
          </h3>
        </div>

        <button
          onClick={() => setShowPasswordModal(true)}
          className={`
            w-full px-4 py-4 rounded-lg text-left transition-colors
            ${theme === 'dark'
              ? 'bg-gray-800 hover:bg-gray-700'
              : 'bg-white hover:bg-gray-50'
            }
          `}
        >
          <p className={`font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Change Password
          </p>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Last changed 3 months ago
          </p>
        </button>

        {showPasswordModal && (
          <div className={`
            fixed inset-0 z-50 flex items-center justify-center p-4
            bg-black/50
          `}>
            <div className={`
              w-full max-w-md rounded-lg p-6
              ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
            `}>
              <h4 className={`text-lg font-medium mb-4 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Change Password
              </h4>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={`
                      w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                      ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                      }
                    `}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`
                      w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                      ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                      }
                    `}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`
                      w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                      ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                      }
                    `}
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className={`
                      px-4 py-2 rounded-lg transition-colors
                      ${theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Login Alerts */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-yellow-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Login Alerts
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className={`font-medium ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Email Notifications
            </p>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Get notified of new login attempts
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, loginAlerts: !prev.loginAlerts }))}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full
              transition-colors duration-200 ease-in-out focus:outline-none
              ${settings.loginAlerts
                ? 'bg-blue-600'
                : theme === 'dark'
                  ? 'bg-gray-700'
                  : 'bg-gray-200'
              }
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition-transform duration-200 ease-in-out
                ${settings.loginAlerts ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Trusted Devices */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Smartphone className="h-5 w-5 text-purple-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Trusted Devices
          </h3>
        </div>

        <div className="space-y-4">
          {settings.trustedDevices.map(device => (
            <div
              key={device.id}
              className={`
                flex items-center justify-between p-4 rounded-lg
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
              `}
            >
              <div>
                <p className={`font-medium ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {device.name}
                </p>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Last used {device.lastUsed.toLocaleDateString()} in {device.location}
                </p>
              </div>
              <button
                onClick={() => removeTrustedDevice(device.id)}
                className={`
                  px-3 py-1 rounded-lg text-sm transition-colors
                  ${theme === 'dark'
                    ? 'bg-red-900/20 text-red-400 hover:bg-red-900/30'
                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }
                `}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          className={`
            px-4 py-2 rounded-lg transition-colors
            ${theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          Reset to Default
        </button>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}